import { Injectable, signal, computed } from '@angular/core';
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserSession, CognitoIdToken, CognitoAccessToken, CognitoRefreshToken } from 'amazon-cognito-identity-js';
import { environment } from '../../environments/environment';

type UserGroup = 'guards' | 'bcu' | 'hse' | 'admin' | null;

const userPool = new CognitoUserPool({
  UserPoolId: environment.cognitoUserPoolId,
  ClientId: environment.cognitoClientId,
});

@Injectable({ providedIn: 'root' })
export class AuthService {
  isAuthenticated = signal(false);
  currentUser = signal<CognitoUser | null>(null);
  userGroup = signal<UserGroup>(null);
  private userGroups = signal<string[]>([]);
  userEmail = signal<string>('');
  currentSite = signal<string>('');
  siteOverride = signal<string | null>(
    typeof localStorage !== 'undefined' ? localStorage.getItem('tm-site-override') : null
  );
  isLoading = signal(false);
  loginError = signal<string | null>(null);

  hasGroup = (...groups: string[]): boolean => {
    const g = this.userGroups();
    return g.some(ug => groups.includes(ug));
  };

  userName = computed(() => this.userEmail() || '');

  async login(email: string, password: string): Promise<void> {
    this.isLoading.set(true);
    this.loginError.set(null);

    const user = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    return new Promise<void>((resolve, reject) => {
      user.authenticateUser(authDetails, {
        onSuccess: (session) => {
          this.setSession(user, session);
          this.isLoading.set(false);
          resolve();
        },
        onFailure: (err) => {
          this.loginError.set(err.message || 'Authentication failed');
          this.isLoading.set(false);
          reject(err);
        },
        newPasswordRequired: () => {
          user.completeNewPasswordChallenge(password, {}, {
            onSuccess: (session) => {
              this.setSession(user, session);
              this.isLoading.set(false);
              resolve();
            },
            onFailure: (err) => {
              this.loginError.set(err.message || 'Password challenge failed');
              this.isLoading.set(false);
              reject(err);
            },
          });
        },
      });
    });
  }

  logout(): void {
    const user = this.currentUser();
    if (user) user.signOut();
    this.isAuthenticated.set(false);
    this.currentUser.set(null);
    this.userGroup.set(null);
    this.userGroups.set([]);
    this.siteOverride.set(null);
    localStorage.removeItem('tm-site-override');
    // Clear all Cognito tokens
    Object.keys(localStorage).filter(k => k.startsWith('CognitoIdentityServiceProvider')).forEach(k => localStorage.removeItem(k));
    // Redirect to Cognito logout to also end EntraID session
    if (window.location.hostname !== 'localhost') {
      const logoutUrl = `https://${environment.cognitoDomain}/logout?client_id=${environment.cognitoClientId}&logout_uri=${encodeURIComponent(window.location.origin)}`;
      window.location.href = logoutUrl;
    }
  }

  async getIdToken(): Promise<string> {
    const user = this.currentUser();
    if (!user) throw new Error('No user');
    return new Promise((resolve, reject) => {
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) return reject(err || new Error('No session'));
        resolve(session.getIdToken().getJwtToken());
      });
    });
  }

  async checkSession(): Promise<void> {
    const user = userPool.getCurrentUser();
    if (!user) return;
    return new Promise<void>((resolve) => {
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (!err && session && session.isValid()) {
          this.setSession(user, session);
        }
        resolve();
      });
    });
  }

  async setTokens(tokens: { id_token: string; access_token: string; refresh_token?: string }): Promise<void> {
    const idToken = new CognitoIdToken({ IdToken: tokens.id_token });
    const accessToken = new CognitoAccessToken({ AccessToken: tokens.access_token });
    const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refresh_token || '' });
    const session = new CognitoUserSession({ IdToken: idToken, AccessToken: accessToken, RefreshToken: refreshToken });
    const payload = idToken.decodePayload();
    const username = payload['cognito:username'] || payload['sub'] || 'sso-user';
    const user = new CognitoUser({ Username: username, Pool: userPool });
    user.setSignInUserSession(session);
    // Manually persist tokens in localStorage (SDK doesn't do it for manual sessions)
    const keyPrefix = `CognitoIdentityServiceProvider.${userPool.getClientId()}.${username}`;
    localStorage.setItem(`${keyPrefix}.idToken`, tokens.id_token);
    localStorage.setItem(`${keyPrefix}.accessToken`, tokens.access_token);
    localStorage.setItem(`${keyPrefix}.refreshToken`, tokens.refresh_token || '');
    localStorage.setItem(`${keyPrefix}.clockDrift`, '0');
    localStorage.setItem(`CognitoIdentityServiceProvider.${userPool.getClientId()}.LastAuthUser`, username);
    this.setSession(user, session);
  }

  private setSession(user: CognitoUser, session: CognitoUserSession): void {
    this.currentUser.set(user);
    this.isAuthenticated.set(true);
    const payload = session.getIdToken().decodePayload();
    const groups: string[] = payload['cognito:groups'] || [];
    // Derive groups from custom attributes (EntraID SAML)
    if (payload['custom:is_guard'] === 'true' && !groups.includes('guards')) groups.push('guards');
    if (payload['custom:is_bcu'] === 'true' && !groups.includes('bcu')) groups.push('bcu');
    if (payload['custom:is_hse'] === 'true' && !groups.includes('hse')) groups.push('hse');
    if (payload['custom:is_admin'] === 'true' && !groups.includes('admin')) groups.push('admin');
    if (payload['custom:is_super_admin'] === 'true' && !groups.includes('super_admin')) groups.push('super_admin');
    // Default to guards if no group
    if (groups.length === 0) groups.push('guards');
    this.userGroup.set((groups[0] as UserGroup) || null);
    this.userGroups.set(groups);
    this.userEmail.set(payload['email'] || user.getUsername());
    this.currentSite.set(payload['custom:site'] || '');
  }

  get effectiveSite(): string {
    return this.siteOverride() || this.currentSite();
  }

  switchSite(site: string | null): void {
    if (site === this.currentSite() || !site) {
      this.siteOverride.set(null);
      localStorage.removeItem('tm-site-override');
    } else {
      this.siteOverride.set(site);
      localStorage.setItem('tm-site-override', site);
    }
  }
}
